import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpEvent,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse,
  HttpResponse,
  HttpClient,
  HttpContextToken
} from '@angular/common/http';
import { EMPTY, Observable, from, throwError } from 'rxjs';
import { catchError, tap, switchMap } from 'rxjs/operators';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { ApiResponseType } from '../../shared/typings/api-response.type';
import { CreditLimitModalComponent } from '../credit-limit-modal/credit-limit-modal.component';

/**
 * Set on a request whose "not your assigned request" refusal the caller handles
 * itself (e.g. loading a dependant's details in the Application review pop-up):
 * the error is passed back instead of leaving the page for /pages/not-assigned.
 */
export const HANDLE_NOT_ASSIGNED_LOCALLY = new HttpContextToken<boolean>(() => false);

/**
 * Callers receive the response body ({ statusCode, message, ... }). Many of them
 * read the reason as `err.error.message` (the Angular error shape), which was
 * undefined, so their fallback text ("Failed to ... Please try again") replaced
 * this interceptor's toast carrying the server's reason (the toastr shows one
 * toast at a time). The body is passed on unchanged, plus a hidden `error` copy
 * of it, so both `err.message` and `err.error.message` give the
 * server's reason.
 */
function withNestedError(body: any): any {
  if (body && typeof body === 'object' && !('error' in body)) {
    // A copy (not the body itself), so `err.error.error` stays undefined as before.
    Object.defineProperty(body, 'error', { value: { ...body }, enumerable: false, configurable: true });
  }
  return body;
}

@Injectable()
export class ErrorHandlingInterceptor implements HttpInterceptor {
  private apiUrl = `${environment.apiBase}`;

  constructor(
    private toastrService: NbToastrService,
    private router: Router,
    private http: HttpClient,
    private dialogService: NbDialogService
  ) {}

  intercept(httpRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const httpMethod = httpRequest.method,
      isRefreshTokenEndpoint = httpRequest.url.includes('/users/refreshToken'),
      isNotificationToggleRead =
        httpRequest.url.includes('/board/notifications/') &&
        httpRequest.url.includes('/toggle-read'),
      // What's next box logs a WhatsApp copy in the background; the box shows its own toast.
      isContactLog = httpRequest.url.includes('/contact-log'),
      // The Complete your profile page shows its own success / error on the card.
      isOwnDateOfBirth =
        httpRequest.url.includes('/board/users/me/date-of-birth') ||
        httpRequest.url.includes('/board/birthdays/popup-seen'),
      // A PDF preview saves nothing, so no "done" message.
      isPreviewOnly = httpRequest.url.includes('/hotel-confirmation/preview');

    return next.handle(httpRequest).pipe(
      tap((event: HttpResponse<void>) => {
        const isUploadProcess = event.url === `${environment.apiBase}/upload`;
        if (
          !['GET', 'OPTIONS'].includes(httpMethod) &&
          [200, 201].includes(event.status) &&
          !isUploadProcess &&
          !isRefreshTokenEndpoint &&
          !isNotificationToggleRead &&
          !isContactLog &&
          !isOwnDateOfBirth &&
          !isPreviewOnly
        ) {
          this.toastrService.success('Operation done successfully', 'Done!', {
            icon: 'checkmark-outline'
          });
          return EMPTY;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 0) {
          console.log('----------- ERROR: API CONNECTION -----------', error);
          this.toastrService.danger('Please check your API connection!', 'Error!', {
            icon: 'alert-circle-outline'
          });
          return EMPTY;
        } else if (error.status === 401) {
          return isRefreshTokenEndpoint ? this.logout(error) : this.refreshToken(httpRequest, next);
        } else if (error.status === 423) {
          this.router.navigateByUrl('/locked');
          return EMPTY;
        } else if (
          error.status === 403 &&
          ['REQUEST_NOT_ASSIGNED', 'GROUP_NOT_ASSIGNED'].includes(error.error?.code) &&
          httpRequest.context.get(HANDLE_NOT_ASSIGNED_LOCALLY)
        ) {
          // The caller shows its own message — no redirect, no toast.
          return throwError(() => withNestedError(error.error));
        } else if (
          error.status === 403 &&
          ['REQUEST_NOT_ASSIGNED', 'GROUP_NOT_ASSIGNED'].includes(error.error?.code)
        ) {
          // Opened a request/group that isn't assigned to this user (e.g. typed the
          // number into the address bar). Show the dedicated page, not a toast.
          // Several calls on the same page may be refused; navigating is idempotent.
          // `details` names the case manager to talk to (staff only); it rides in
          // the navigation state so it isn't in the address bar.
          this.router.navigate(['/pages/not-assigned'], {
            queryParams: error.error?.code === 'GROUP_NOT_ASSIGNED' ? { type: 'group' } : {},
            replaceUrl: true,
            state: { notAssigned: error.error?.details ?? {} }
          });
          return EMPTY;
        } else if (error.error instanceof Blob) {
          // A file request (responseType 'blob') that failed: its error body is
          // a Blob, so read the server's reason from it before the toast.
          return from(error.error.text()).pipe(
            switchMap((text) => {
              let body: any = {};
              try {
                body = JSON.parse(text);
              } catch {
                /* not JSON: the generic message */
              }
              this.toastrService.danger(body?.message || 'Something went wrong!', 'Error!', {
                icon: 'alert-circle-outline'
              });
              return throwError(() => withNestedError(body));
            })
          );
        } else {
          const code = error.error?.code;
          if (
            code === 'ENTITY_CREDIT_LIMIT_EXCEEDED' ||
            code === 'ENTITY_CREDIT_LIMIT_REACHED'
          ) {
            // Credit-limit block gets a dedicated centered modal (with figures +
            // "Check entity requests"), not the generic error toast.
            this.dialogService.open(CreditLimitModalComponent, {
              context: {
                message:
                  error.error?.message ||
                  "This request would exceed the entity's credit limit.",
                details: error.error?.details || null
              },
              closeOnBackdropClick: true
            });
            return throwError(() => withNestedError(error.error));
          }
          if (code === 'ENTITY_PAYMENT_OVERDUE') {
            // Payment due date passed with earlier requests unpaid: same modal.
            this.dialogService.open(CreditLimitModalComponent, {
              context: {
                title: 'Payment overdue',
                message: error.error?.message || 'Payment for this entity is overdue.',
                details: error.error?.details || null
              },
              closeOnBackdropClick: true
            });
            return throwError(() => withNestedError(error.error));
          }
          if (isOwnDateOfBirth) return throwError(() => withNestedError(error.error));
          this.toastrService.danger(error.error.message || 'Something went wrong!', 'Error!', {
            icon: 'alert-circle-outline'
          });
          return throwError(() => withNestedError(error.error));
        }
      })
    );
  }

  private refreshToken(httpRequest: HttpRequest<any>, next: HttpHandler) {
    return this.http.post(`${this.apiUrl}/users/refreshToken`, {}).pipe(
      switchMap((res: ApiResponseType<any>) => {
        if (res.statusCode === 200) {
          localStorage.setItem(
            'auth_app_token',
            JSON.stringify({
              createdAt: Date.now(),
              name: 'nb:auth:simple:token',
              ownerStrategyName: 'email',
              value: res.data.token
            })
          );
          // Keep the new refresh token too. The AuthInterceptor sends the one in
          // the saved user, so without this every refresh reused the login one
          // and staff were logged out 15 days after signing in.
          const savedUser = localStorage.getItem('user');
          if (savedUser && res.data.refreshToken) {
            try {
              const user = JSON.parse(savedUser);
              user.refreshToken = res.data.refreshToken;
              localStorage.setItem('user', JSON.stringify(user));
            } catch {
              /* unreadable saved user: leave it as it was */
            }
          }
        }
        const clonedReq = httpRequest.clone({
          setHeaders: { Authorization: res.data.token }
        });
        return next.handle(clonedReq);
      })
    );
  }

  private logout(error: HttpErrorResponse) {
    localStorage.removeItem('user');
    localStorage.removeItem('auth_app_token');
    this.router.navigateByUrl('/auth/login');
    this.toastrService.danger(error.error?.message, 'Error!', {
      icon: 'alert-circle-outline'
    });
    return EMPTY;
  }
}
